/**
 * Shared axis-aligned box geometry utilities for compound generators.
 *
 * Extracted so that multiple generators (room, chair, …) can build
 * concatenated-box meshes without duplicating the vertex/normal/UV logic.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre, matching PoseRef / Blender.
 */

import * as THREE from "three";
import { recomputeLoopNormals } from "../meshOps.ts";
import type { PrimitiveGeometry } from "../primitives.ts";
import { generateCylinder } from "../primitives.ts";
import {
  buildEdgesAndCornerEdges,
  concatTopology,
  triangulateTopologyForRendering,
} from "../topology.ts";
import type { PolygonTopology } from "../types.ts";
import {
  asLoopNormals,
  cloneRenderTriangleIndices,
  EMPTY_RENDER_TRIANGLE_INDICES,
} from "../types.ts";

// ---------------------------------------------------------------------------
// BoxSpec — placement descriptor for one segment inside a concatenated mesh
// ---------------------------------------------------------------------------

/** Centre + half-extents for one axis-aligned box segment. */
export interface BoxSpec {
  cx: number;
  cy: number;
  cz: number;
  sx: number;
  sy: number;
  sz: number;
}

// ---------------------------------------------------------------------------
// buildBox — one axis-aligned box centred at the local origin
// ---------------------------------------------------------------------------

/**
 * Build a single axis-aligned box of the given dimensions centred at the
 * local origin. Winding and face order match `generateCube` in `primitives.ts`.
 *
 * UVs are per-face and scale with the world dimensions so the grid overlay
 * reads at a consistent world scale across surfaces of different sizes.
 */
export function buildBox(
  sizeX: number,
  sizeY: number,
  sizeZ: number,
): PrimitiveGeometry {
  const hx = sizeX * 0.5;
  const hy = sizeY * 0.5;
  const hz = sizeZ * 0.5;

  // 8 unique shared vertices
  // prettier-ignore
  const positions = new Float32Array([
    -hx,
    -hy,
    -hz, // 0
    hx,
    -hy,
    -hz, // 1
    hx,
    hy,
    -hz, // 2
    -hx,
    hy,
    -hz, // 3
    -hx,
    -hy,
    hz, // 4
    hx,
    -hy,
    hz, // 5
    hx,
    hy,
    hz, // 6
    -hx,
    hy,
    hz, // 7
  ]);

  // 6 quad faces, same winding convention as generateCube().
  // prettier-ignore
  const faceVerts = new Uint32Array([
    4,
    5,
    6,
    7, // +Z (top)
    3,
    2,
    1,
    0, // -Z (bottom)
    1,
    2,
    6,
    5, // +X (right)
    3,
    0,
    4,
    7, // -X (left)
    2,
    3,
    7,
    6, // +Y (front)
    0,
    1,
    5,
    4, // -Y (back)
  ]);

  const offsets = new Uint32Array([0, 4, 8, 12, 16, 20, 24]);
  const topology: PolygonTopology = {
    offsets,
    corners: faceVerts,
    ...buildEdgesAndCornerEdges(offsets, faceVerts),
  };

  // prettier-ignore
  const loopNorms = new Float32Array([
    0,
    0,
    1,
    0,
    0,
    1,
    0,
    0,
    1,
    0,
    0,
    1, // +Z
    0,
    0,
    -1,
    0,
    0,
    -1,
    0,
    0,
    -1,
    0,
    0,
    -1, // -Z
    1,
    0,
    0,
    1,
    0,
    0,
    1,
    0,
    0,
    1,
    0,
    0, // +X
    -1,
    0,
    0,
    -1,
    0,
    0,
    -1,
    0,
    0,
    -1,
    0,
    0, // -X
    0,
    1,
    0,
    0,
    1,
    0,
    0,
    1,
    0,
    0,
    1,
    0, // +Y
    0,
    -1,
    0,
    0,
    -1,
    0,
    0,
    -1,
    0,
    0,
    -1,
    0, // -Y
  ]);

  const uvs = new Float32Array(48);
  const uvForFace = (u: number, v: number, i: number) => {
    uvs[i * 8 + 0] = 0;
    uvs[i * 8 + 1] = 0;
    uvs[i * 8 + 2] = u;
    uvs[i * 8 + 3] = 0;
    uvs[i * 8 + 4] = u;
    uvs[i * 8 + 5] = v;
    uvs[i * 8 + 6] = 0;
    uvs[i * 8 + 7] = v;
  };
  uvForFace(sizeX, sizeY, 0); // +Z
  uvForFace(sizeX, sizeY, 1); // -Z
  uvForFace(sizeY, sizeZ, 2); // +X
  uvForFace(sizeY, sizeZ, 3); // -X
  uvForFace(sizeX, sizeZ, 4); // +Y
  uvForFace(sizeX, sizeZ, 5); // -Y

  return {
    vertices: positions,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: asLoopNormals(loopNorms),
    topology,
    uvs,
  };
}

// ---------------------------------------------------------------------------
// buildCylinder — thin wrapper around generateCylinder for generators
// ---------------------------------------------------------------------------

/**
 * Build a cylinder of the given radius and height. The cylinder is centred at
 * the local origin (bottom at -height/2, top at +height/2). Segments default
 * to 24 — sufficient for a clean silhouette on lamp poles / bases while
 * keeping the triangle count modest.
 *
 * This is the canonical way for generators in this directory to get cylinder
 * geometry — it avoids each generator taking a direct dependency on the lower-
 * level `generateCylinder` in `primitives.ts`.
 */
export function buildCylinder(
  radius: number,
  height: number,
  segments = 24,
): PrimitiveGeometry {
  return generateCylinder({ radius, height, segments });
}

export function transformGeometry(
  geometry: PrimitiveGeometry,
  position: [number, number, number] = [0, 0, 0],
  rotation: [number, number, number] = [0, 0, 0],
): PrimitiveGeometry {
  const matrix = new THREE.Matrix4().makeRotationFromEuler(
    new THREE.Euler(rotation[0], rotation[1], rotation[2], "XYZ"),
  );
  matrix.setPosition(position[0], position[1], position[2]);
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrix);

  const vertices = new Float32Array(geometry.vertices.length);
  const loopNormals = new Float32Array(geometry.loopNormals.length);
  const v = new THREE.Vector3();

  for (let i = 0; i < geometry.vertices.length; i += 3) {
    v.set(
      geometry.vertices[i],
      geometry.vertices[i + 1],
      geometry.vertices[i + 2],
    ).applyMatrix4(matrix);
    vertices[i] = v.x;
    vertices[i + 1] = v.y;
    vertices[i + 2] = v.z;
  }

  for (let i = 0; i < geometry.loopNormals.length; i += 3) {
    v.set(
      geometry.loopNormals[i],
      geometry.loopNormals[i + 1],
      geometry.loopNormals[i + 2],
    )
      .applyMatrix3(normalMatrix)
      .normalize();
    loopNormals[i] = v.x;
    loopNormals[i + 1] = v.y;
    loopNormals[i + 2] = v.z;
  }

  return {
    vertices,
    indices: cloneRenderTriangleIndices(geometry.indices),
    loopNormals: asLoopNormals(loopNormals),
    topology: {
      offsets: new Uint32Array(geometry.topology.offsets),
      corners: new Uint32Array(geometry.topology.corners),
      edges: new Uint32Array(geometry.topology.edges),
      cornerEdges: new Uint32Array(geometry.topology.cornerEdges),
      edgeFlags: new Uint8Array(geometry.topology.edgeFlags),
    },
    uvs: new Float32Array(geometry.uvs),
  };
}

export function concatPrimitiveGeometries(
  parts: readonly PrimitiveGeometry[],
): PrimitiveGeometry {
  if (parts.length === 0) return emptyGeometry();
  if (parts.length === 1) return parts[0];

  let totalVertexFloats = 0;
  let totalLoopNormalFloats = 0;
  let totalUvFloats = 0;
  for (const part of parts) {
    totalVertexFloats += part.vertices.length;
    totalLoopNormalFloats += part.loopNormals.length;
    totalUvFloats += part.uvs.length;
  }

  const vertices = new Float32Array(totalVertexFloats);
  const loopNormals = new Float32Array(totalLoopNormalFloats);
  const uvs = new Float32Array(totalUvFloats);

  let topology = parts[0].topology;
  vertices.set(parts[0].vertices, 0);
  loopNormals.set(parts[0].loopNormals, 0);
  uvs.set(parts[0].uvs, 0);

  let vertexFloatOffset = parts[0].vertices.length;
  let loopNormalOffset = parts[0].loopNormals.length;
  let uvOffset = parts[0].uvs.length;

  for (let i = 1; i < parts.length; i++) {
    const part = parts[i];
    vertices.set(part.vertices, vertexFloatOffset);
    loopNormals.set(part.loopNormals, loopNormalOffset);
    uvs.set(part.uvs, uvOffset);
    topology = concatTopology(topology, part.topology, vertexFloatOffset / 3);
    vertexFloatOffset += part.vertices.length;
    loopNormalOffset += part.loopNormals.length;
    uvOffset += part.uvs.length;
  }

  return {
    vertices,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: asLoopNormals(loopNormals),
    topology,
    uvs,
  };
}

// ---------------------------------------------------------------------------
// emptyGeometry — zero-triangle fallback
// ---------------------------------------------------------------------------

/** Empty geometry — used when a piece is fully suppressed (e.g. zero-length legs). */
export function emptyGeometry(): PrimitiveGeometry {
  return {
    vertices: new Float32Array(0),
    indices: EMPTY_RENDER_TRIANGLE_INDICES,
    loopNormals: asLoopNormals(new Float32Array(0)),
    topology: {
      offsets: new Uint32Array([0]),
      corners: new Uint32Array(0),
      edges: new Uint32Array(0),
      cornerEdges: new Uint32Array(0),
      edgeFlags: new Uint8Array(0),
    },
    uvs: new Float32Array(0),
  };
}

// ---------------------------------------------------------------------------
// buildDisc — flat filled circle, double-sided
// ---------------------------------------------------------------------------

/**
 * Build a flat filled disc of the given radius centred at the local origin in
 * the XY plane (Z = 0). The disc is double-sided: N upward-facing triangles
 * (+Z normals) and N downward-facing triangles (−Z normals), fan-triangulated
 * from a single centre vertex.
 *
 * Use this wherever a zero-thickness flat surface is more appropriate than an
 * extruded solid — the classic case being a liquid waterline inside a vessel.
 *
 * @param radius   Disc radius in metres.
 * @param segments Vertex count around the perimeter (clamped to ≥ 3).
 */
export function buildDisc(radius: number, segments: number): PrimitiveGeometry {
  const N = Math.max(3, Math.round(segments));
  const R = Math.max(1e-4, radius);

  // Centre vertex (index 0) + N perimeter vertices (indices 1…N).
  const verts = new Float32Array((N + 1) * 3);
  for (let i = 0; i < N; i++) {
    const theta = (2 * Math.PI * i) / N;
    verts[(i + 1) * 3] = Math.cos(theta) * R;
    verts[(i + 1) * 3 + 1] = Math.sin(theta) * R;
    verts[(i + 1) * 3 + 2] = 0;
  }
  // verts[0..2] = (0, 0, 0) — already zero from Float32Array initialisation.

  // 2N triangular faces: N top (+Z) + N bottom (−Z, reversed winding).
  const nFaces = 2 * N;
  const nCorners = 2 * N * 3;
  const offsets = new Uint32Array(nFaces + 1);
  const corners = new Uint32Array(nCorners);
  let ci = 0,
    fi = 0;

  for (let i = 0; i < N; i++) {
    const a = i + 1;
    const b = ((i + 1) % N) + 1;
    // Top face — CCW from +Z: (centre, a, b)
    offsets[fi++] = ci;
    corners[ci++] = 0;
    corners[ci++] = a;
    corners[ci++] = b;
    // Bottom face — CCW from −Z: (centre, b, a)
    offsets[fi++] = ci;
    corners[ci++] = 0;
    corners[ci++] = b;
    corners[ci++] = a;
  }
  offsets[fi] = ci;

  const topology: PolygonTopology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };

  // Flat normals: top faces +Z, bottom faces −Z.
  const loopNorms = new Float32Array(nCorners * 3);
  for (let i = 0; i < N; i++) {
    // Top face corners
    loopNorms[(i * 2 * 3 + 0) * 3 + 2] = 1;
    loopNorms[(i * 2 * 3 + 1) * 3 + 2] = 1;
    loopNorms[(i * 2 * 3 + 2) * 3 + 2] = 1;
    // Bottom face corners
    loopNorms[(i * 2 * 3 + 3) * 3 + 2] = -1;
    loopNorms[(i * 2 * 3 + 4) * 3 + 2] = -1;
    loopNorms[(i * 2 * 3 + 5) * 3 + 2] = -1;
  }

  // UVs: planar XY projection, mapped to [0, 1] from (−R, −R) to (+R, +R).
  const uvs = new Float32Array(nCorners * 2);
  const toUV = (vi: number, ui: number) => {
    uvs[ui] = (verts[vi * 3] / R + 1) * 0.5;
    uvs[ui + 1] = (verts[vi * 3 + 1] / R + 1) * 0.5;
  };
  let ui = 0;
  for (let i = 0; i < N; i++) {
    const a = i + 1;
    const b = ((i + 1) % N) + 1;
    toUV(0, ui);
    ui += 2;
    toUV(a, ui);
    ui += 2;
    toUV(b, ui);
    ui += 2; // top
    toUV(0, ui);
    ui += 2;
    toUV(b, ui);
    ui += 2;
    toUV(a, ui);
    ui += 2; // bottom
  }

  return {
    vertices: verts,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: asLoopNormals(loopNorms),
    topology,
    uvs,
  };
}

// ---------------------------------------------------------------------------
// concatBoxes — merge N box specs into one PrimitiveGeometry
// ---------------------------------------------------------------------------

/**
 * Concatenate N axis-aligned box geometries into one `PrimitiveGeometry`.
 * Each box contributes exactly 8 verts / 6 quads / 24 corners, so the
 * combined arrays are exact linear functions of the box count — no
 * reallocation required.
 *
 * Boxes are emitted as disjoint volumes (no welding along shared walls),
 * which keeps the topology unambiguous and matches how the renderer treats
 * flat-shaded hard-surface geometry.
 */
export function concatBoxes(boxes: readonly BoxSpec[]): PrimitiveGeometry {
  const n = boxes.length;
  if (n === 0) return emptyGeometry();

  const vertices = new Float32Array(n * 8 * 3);
  const corners = new Uint32Array(n * 24);
  const offsets = new Uint32Array(n * 6 + 1);
  const loopNorms = new Float32Array(n * 24 * 3);
  const uvs = new Float32Array(n * 24 * 2);

  for (let i = 0; i < n; i++) {
    const spec = boxes[i];
    const unit = buildBox(spec.sx, spec.sy, spec.sz);
    const vOff = i * 8;

    // Translate vertices into place
    for (let j = 0; j < 8; j++) {
      vertices[(vOff + j) * 3 + 0] = unit.vertices[j * 3 + 0] + spec.cx;
      vertices[(vOff + j) * 3 + 1] = unit.vertices[j * 3 + 1] + spec.cy;
      vertices[(vOff + j) * 3 + 2] = unit.vertices[j * 3 + 2] + spec.cz;
    }

    // Face corners — each quad has 4 corners, indexed into local verts
    const cOff = i * 24;
    for (let k = 0; k < 24; k++)
      corners[cOff + k] = unit.topology.corners[k] + vOff;

    // Face offset starts (6 faces of 4 corners each)
    for (let f = 0; f < 6; f++) offsets[i * 6 + f] = (i * 6 + f) * 4;

    // Per-corner normals and UVs copy verbatim — each box's flat normals are
    // already correct for its own orientation, no remapping needed.
    loopNorms.set(unit.loopNormals, cOff * 3);
    if (unit.uvs) uvs.set(unit.uvs, cOff * 2);
  }
  offsets[n * 6] = n * 24;

  const topology: PolygonTopology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };

  return {
    vertices,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: asLoopNormals(loopNorms),
    topology,
    uvs,
  };
}

// ---------------------------------------------------------------------------
// buildRingDisc — flat annular disc (washer / hat brim)
// ---------------------------------------------------------------------------

/**
 * Build a flat ring disc (washer / annulus) centred at the local origin.
 *
 * Use this for hat brims, washers, rings, or any flat torus-like shape where
 * `buildFrustumShell` would produce unwanted solid-disc caps.
 *
 * Surface topology (all faces outward-facing, normals via `recomputeLoopNormals`):
 *   • Top ring    — N quads, normals +Z  (Z = thickness)
 *   • Bottom ring — N quads, normals −Z  (Z = 0)
 *   • Outer wall  — N quads, normals radially outward
 *   • Inner wall  — N quads, normals radially inward
 *
 * With `autoSmooth` shading the 90° corner angles between the flat rings and the
 * cylindrical walls resolve to sharp edges, giving a clean chamfered-brim look.
 *
 * @param outerRadius  Outer radius (metres).
 * @param innerRadius  Inner hole radius (metres).
 * @param thickness    Disc height from Z = 0 to Z = thickness (metres).
 * @param segments     Vertex count around the circumference (≥ 3).
 */
export function buildRingDisc(
  outerRadius: number,
  innerRadius: number,
  thickness: number,
  segments: number,
): PrimitiveGeometry {
  const N = Math.max(3, Math.round(segments));
  const OR = Math.max(innerRadius + 1e-4, outerRadius);
  const IR = Math.max(1e-4, innerRadius);
  const T = Math.max(1e-4, thickness);

  // Four vertex rings, each of N vertices at angles θ = 2πi/N:
  //   [0   .. N-1]   top  outer  (Z = T)
  //   [N   .. 2N-1]  top  inner  (Z = T)
  //   [2N  .. 3N-1]  bot  outer  (Z = 0)
  //   [3N  .. 4N-1]  bot  inner  (Z = 0)
  const verts = new Float32Array(4 * N * 3);
  for (let i = 0; i < N; i++) {
    const theta = (2 * Math.PI * i) / N;
    const cos = Math.cos(theta);
    const sin_ = Math.sin(theta);
    // top outer
    verts[i * 3] = cos * OR;
    verts[i * 3 + 1] = sin_ * OR;
    verts[i * 3 + 2] = T;
    // top inner
    verts[(N + i) * 3] = cos * IR;
    verts[(N + i) * 3 + 1] = sin_ * IR;
    verts[(N + i) * 3 + 2] = T;
    // bot outer
    verts[(2 * N + i) * 3] = cos * OR;
    verts[(2 * N + i) * 3 + 1] = sin_ * OR;
    verts[(2 * N + i) * 3 + 2] = 0;
    // bot inner
    verts[(3 * N + i) * 3] = cos * IR;
    verts[(3 * N + i) * 3 + 1] = sin_ * IR;
    verts[(3 * N + i) * 3 + 2] = 0;
  }

  const nFaces = 4 * N;
  const nCorners = 4 * nFaces; // every face is a quad
  const offsets = new Uint32Array(nFaces + 1);
  const corners = new Uint32Array(nCorners);
  let ci = 0,
    fi = 0;

  for (let i = 0; i < N; i++) {
    const i1 = (i + 1) % N;

    // Top ring — CCW from above → normal +Z
    offsets[fi++] = ci;
    corners[ci++] = i;
    corners[ci++] = i1;
    corners[ci++] = N + i1;
    corners[ci++] = N + i;

    // Bottom ring — CW from above → normal −Z
    offsets[fi++] = ci;
    corners[ci++] = 3 * N + i;
    corners[ci++] = 3 * N + i1;
    corners[ci++] = 2 * N + i1;
    corners[ci++] = 2 * N + i;

    // Outer wall — CCW from outside → normals radially outward
    offsets[fi++] = ci;
    corners[ci++] = 2 * N + i;
    corners[ci++] = 2 * N + i1;
    corners[ci++] = i1;
    corners[ci++] = i;

    // Inner wall — CCW from inside → normals radially inward
    offsets[fi++] = ci;
    corners[ci++] = N + i;
    corners[ci++] = N + i1;
    corners[ci++] = 3 * N + i1;
    corners[ci++] = 3 * N + i;
  }
  offsets[fi] = ci;

  const topology: PolygonTopology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };

  // UVs: u = azimuth fraction [0, 1], v = radial / height fraction [0, 1]
  const uvs = new Float32Array(nCorners * 2);
  let ui = 0;
  for (let i = 0; i < N; i++) {
    const u0 = i / N,
      u1 = (i + 1) / N;

    // Top ring  (outer=v0, inner=v1)
    uvs[ui++] = u0;
    uvs[ui++] = 0;
    uvs[ui++] = u1;
    uvs[ui++] = 0;
    uvs[ui++] = u1;
    uvs[ui++] = 1;
    uvs[ui++] = u0;
    uvs[ui++] = 1;

    // Bottom ring  (inner_bot[i], inner_bot[i1], outer_bot[i1], outer_bot[i])
    uvs[ui++] = u0;
    uvs[ui++] = 1;
    uvs[ui++] = u1;
    uvs[ui++] = 1;
    uvs[ui++] = u1;
    uvs[ui++] = 0;
    uvs[ui++] = u0;
    uvs[ui++] = 0;

    // Outer wall  (bot→top, v=0 at base)
    uvs[ui++] = u0;
    uvs[ui++] = 0;
    uvs[ui++] = u1;
    uvs[ui++] = 0;
    uvs[ui++] = u1;
    uvs[ui++] = 1;
    uvs[ui++] = u0;
    uvs[ui++] = 1;

    // Inner wall  (top→bot, v=1 at top)
    uvs[ui++] = u0;
    uvs[ui++] = 1;
    uvs[ui++] = u1;
    uvs[ui++] = 1;
    uvs[ui++] = u1;
    uvs[ui++] = 0;
    uvs[ui++] = u0;
    uvs[ui++] = 0;
  }

  return {
    vertices: verts,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: recomputeLoopNormals(verts, topology),
    topology,
    uvs,
  };
}

// ---------------------------------------------------------------------------
// buildExtrudedPolygon — extrude an arbitrary 2D polygon into a flat 3D plate
// ---------------------------------------------------------------------------

/** A 2D point used to describe polygon outlines. */
export type Point2 = readonly [number, number];

/**
 * Extrude a 2D polygon into a flat 3D plate along +Z.
 *
 * The polygon lives in the XY plane; the extrusion runs from Z = 0 to
 * Z = thickness. Top and bottom faces are triangulated via earcut
 * (`THREE.ShapeUtils.triangulateShape`) so arbitrarily concave outlines and
 * holes render correctly — a plain fan triangulation would produce
 * overlapping triangles on non-convex shapes.
 *
 * This is the general-purpose primitive for any die-cut flat shape: hat
 * visors (D-shapes), nameplate badges, logo plaques, cookie-cutter silhouettes,
 * label rings, and anything else that is "a 2D outline pushed up by T metres".
 * Callers supply the outline (and any holes) as ordered `[x, y]` point lists;
 * winding is auto-detected and normalised internally (outer CCW, holes CW)
 * so callers never have to think about it.
 *
 * Topology:
 *   • Top face    — one earcut triangle per emitted tri, normal +Z
 *   • Bottom face — same triangulation with reversed winding, normal −Z
 *   • Walls       — one quad per loop edge (outer loop + each hole), wound so
 *                   the normal points radially outward from the solid material
 *                   (so hole walls face into the hole, not away from it)
 *
 * The triangulator yields one face per triangle on the top / bottom, so edit
 * mode will see multiple triangular faces instead of a single n-gon. This is
 * a deliberate trade-off: PoseRef's render-time triangulator fans each face,
 * which fails on concave polygons, so a generic extruder has to hand the
 * renderer pre-decomposed convex pieces. Callers that want a single merged
 * face can run a coplanar-merge cleanup op after construction.
 *
 * UVs: top/bottom use a planar mapping into the outline's XY bounding box;
 * walls use `u` = normalised arc-length along the loop, `v` = height fraction.
 *
 * @param outline   Outer boundary as `[x, y]` points (≥ 3). Any winding.
 * @param thickness Plate thickness (Z extent, metres). Must be positive.
 * @param holes     Optional hole loops, each `[x, y]` points (≥ 3). Any winding.
 */
export function buildExtrudedPolygon(
  outline: readonly Point2[],
  thickness: number,
  holes: readonly (readonly Point2[])[] = [],
): PrimitiveGeometry {
  const T = Math.max(1e-4, thickness);
  if (outline.length < 3) return emptyGeometry();

  // ── Winding normalisation ──────────────────────────────────────────────────
  //
  // `THREE.ShapeUtils.triangulateShape` expects the outer contour to be CCW
  // (positive signed area when viewed from +Z) and hole contours to be CW. We
  // measure signed area and reverse any loop that comes in with the wrong
  // winding, so callers can pass either orientation freely.
  const toV2 = (pts: readonly Point2[]): THREE.Vector2[] =>
    pts.map(([x, y]) => new THREE.Vector2(x, y));

  const outlineV2 = toV2(outline);
  if (THREE.ShapeUtils.area(outlineV2) < 0) outlineV2.reverse();

  const holesV2: THREE.Vector2[][] = [];
  for (const h of holes) {
    if (h.length < 3) continue;
    const v = toV2(h);
    if (THREE.ShapeUtils.area(v) > 0) v.reverse();
    holesV2.push(v);
  }

  // ── Earcut triangulation ───────────────────────────────────────────────────
  //
  // Returns an array of `[a, b, c]` index triples into the flattened
  // `[...outlineV2, ...holesV2[0], ...holesV2[1], ...]` point list. With the
  // outline wound CCW, each triangle is CCW when viewed from +Z, so the top
  // face normals come out as +Z without any further work.
  const triangles = THREE.ShapeUtils.triangulateShape(outlineV2, holesV2);
  if (triangles.length === 0) return emptyGeometry();

  // ── Loop layout ────────────────────────────────────────────────────────────
  const loops: THREE.Vector2[][] = [outlineV2, ...holesV2];
  const loopStarts: number[] = [];
  let loopOffset = 0;
  for (const loop of loops) {
    loopStarts.push(loopOffset);
    loopOffset += loop.length;
  }
  const nLoopPoints = loopOffset;

  // Flattened point list — used by the earcut indices and wall UVs.
  const allPoints: THREE.Vector2[] = [];
  for (const loop of loops) allPoints.push(...loop);

  // ── Vertex buffer ──────────────────────────────────────────────────────────
  //
  // Two copies of every outline / hole vertex: first the top layer at Z = T,
  // then the bottom layer at Z = 0. Both layers share the same XY positions,
  // so earcut indices can be re-used directly for top and bottom faces.
  const nVerts = 2 * nLoopPoints;
  const verts = new Float32Array(nVerts * 3);
  {
    let vi = 0;
    for (const p of allPoints) {
      verts[vi * 3] = p.x;
      verts[vi * 3 + 1] = p.y;
      verts[vi * 3 + 2] = T;
      vi++;
    }
    for (const p of allPoints) {
      verts[vi * 3] = p.x;
      verts[vi * 3 + 1] = p.y;
      verts[vi * 3 + 2] = 0;
      vi++;
    }
  }
  const topIdx = (i: number) => i;
  const botIdx = (i: number) => nLoopPoints + i;

  // ── Face layout ────────────────────────────────────────────────────────────
  //
  //   nTris       × top triangles     (normal +Z)
  //   nTris       × bottom triangles  (normal −Z, reversed winding)
  //   nLoopPoints × wall quads        (normal outward from solid)
  const nTris = triangles.length;
  const nWallQuads = nLoopPoints;
  const nFaces = 2 * nTris + nWallQuads;
  const nCorners = 2 * nTris * 3 + nWallQuads * 4;

  const offsets = new Uint32Array(nFaces + 1);
  const corners = new Uint32Array(nCorners);
  let ci = 0,
    fi = 0;

  // Top triangles — CCW from +Z
  for (const [a, b, c] of triangles) {
    offsets[fi++] = ci;
    corners[ci++] = topIdx(a);
    corners[ci++] = topIdx(b);
    corners[ci++] = topIdx(c);
  }

  // Bottom triangles — CCW from −Z (reverse winding)
  for (const [a, b, c] of triangles) {
    offsets[fi++] = ci;
    corners[ci++] = botIdx(a);
    corners[ci++] = botIdx(c);
    corners[ci++] = botIdx(b);
  }

  // Wall quads. Walking an edge (i → i+1) in a loop, the "right of walk"
  // direction is (dy, −dx), and for a CCW outer loop that is outward from the
  // solid; for a CW hole loop it is also outward from the solid (i.e. into
  // the hole). The quad [top(i), bot(i), bot(i+1), top(i+1)] has normal in
  // exactly that direction, so a single winding rule handles both cases.
  for (let li = 0; li < loops.length; li++) {
    const loop = loops[li];
    const base = loopStarts[li];
    const size = loop.length;
    for (let i = 0; i < size; i++) {
      const i1 = (i + 1) % size;
      offsets[fi++] = ci;
      corners[ci++] = topIdx(base + i);
      corners[ci++] = botIdx(base + i);
      corners[ci++] = botIdx(base + i1);
      corners[ci++] = topIdx(base + i1);
    }
  }
  offsets[fi] = ci;

  const topology: PolygonTopology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };

  // ── UVs ────────────────────────────────────────────────────────────────────
  //
  // Top and bottom get a planar XY mapping into the outline's bounding box.
  // Walls get `u` = normalised arc-length along the loop, `v` = 0 at bottom /
  // 1 at top — a sensible default for labelling strips or decorative tape.
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;
  for (const p of allPoints) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const uRange = Math.max(1e-9, maxX - minX);
  const vRange = Math.max(1e-9, maxY - minY);

  const uvs = new Float32Array(nCorners * 2);
  let ui = 0;

  // Top triangle UVs — planar XY mapping.
  for (const [a, b, c] of triangles) {
    for (const idx of [a, b, c]) {
      const p = allPoints[idx];
      uvs[ui++] = (p.x - minX) / uRange;
      uvs[ui++] = (p.y - minY) / vRange;
    }
  }
  // Bottom triangle UVs — same mapping, reversed winding.
  for (const [a, b, c] of triangles) {
    for (const idx of [a, c, b]) {
      const p = allPoints[idx];
      uvs[ui++] = (p.x - minX) / uRange;
      uvs[ui++] = (p.y - minY) / vRange;
    }
  }
  // Wall UVs — u = normalised arc-length along the loop, v = height fraction.
  for (const loop of loops) {
    const size = loop.length;
    const edgeLens: number[] = new Array(size);
    let perimeter = 0;
    for (let i = 0; i < size; i++) {
      const p0 = loop[i];
      const p1 = loop[(i + 1) % size];
      const e = Math.hypot(p1.x - p0.x, p1.y - p0.y);
      edgeLens[i] = e;
      perimeter += e;
    }
    const invPerim = perimeter > 0 ? 1 / perimeter : 1;
    let arcLen = 0;
    for (let i = 0; i < size; i++) {
      const u0 = arcLen * invPerim;
      const u1 = (arcLen + edgeLens[i]) * invPerim;
      // Winding: top(i), bot(i), bot(i+1), top(i+1)
      uvs[ui++] = u0;
      uvs[ui++] = 1;
      uvs[ui++] = u0;
      uvs[ui++] = 0;
      uvs[ui++] = u1;
      uvs[ui++] = 0;
      uvs[ui++] = u1;
      uvs[ui++] = 1;
      arcLen += edgeLens[i];
    }
  }

  return {
    vertices: verts,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: recomputeLoopNormals(verts, topology),
    topology,
    uvs,
  };
}

// ---------------------------------------------------------------------------
// evalFrustumProfile — superellipse profile curve for frustum walls
// ---------------------------------------------------------------------------

/**
 * Evaluate the outer radius of a frustum wall at height fraction `t ∈ [0, 1]`,
 * using the same superellipse curve the Bevel operator uses to shape its
 * cross-edge midpoints.
 *
 * The curve lives in the meridional (z, r) cross-section of the frustum. At
 * each sample point (x, y) on the unit superellipse `|x|^r + |y|^r = 1`:
 *
 *   linear_t = (x − y + 1) / 2        — progress along the straight wall line
 *   arc_bulge = (x + y − 1) / 2       — normalised perpendicular deviation
 *
 * Projected onto the radial axis, the perpendicular component equals
 * `arc_bulge × height` (the `wallLen` factors cancel), so the result is:
 *
 *   r(t) = r0 + (r1 − r0) × linear_t + arc_bulge × height
 *
 * The curve always passes through (r0, r1) at the endpoints; only the
 * intermediate shape changes with `profile`.
 *
 * @param t       Height fraction [0, 1] (0 = base, 1 = top).
 * @param r0      Outer radius at the base (t = 0).
 * @param r1      Outer radius at the top  (t = 1).
 * @param height  Total wall height in metres (used to scale the bulge).
 * @param profile Profile in [−1, 1]:
 *                  0  = linear   (straight frustum, superellipse r = 1)
 *                 +1  = convex   (wall bows outward,  r = 4)
 *                 −1  = concave  (wall curves inward,  r = 0.25)
 */
export function evalFrustumProfile(
  t: number,
  r0: number,
  r1: number,
  height: number,
  profile: number,
): number {
  if (Math.abs(profile) < 1e-6 || t <= 0 || t >= 1) {
    return r0 + (r1 - r0) * t;
  }

  // Superellipse exponent: 4^profile
  //   profile = 0  → r = 1  (x + y = 1, linear)
  //   profile = +1 → r = 4  (strongly convex)
  //   profile = −1 → r = ¼  (concave)
  const r = 4 ** profile;

  // Point on superellipse parameterised by x = t (not arc-length, but
  // sufficient for smooth visual shaping with uniform z-spacing).
  const x = t;
  const tR = t ** r;
  const y = tR >= 1 ? 0 : (1 - tR) ** (1 / r);

  const linearT = (x - y + 1) * 0.5;
  const bulgeN = (x + y - 1) * 0.5;

  return Math.max(0.001, r0 + (r1 - r0) * linearT + bulgeN * height);
}

// ---------------------------------------------------------------------------
// buildLatheGeometry — surface of revolution from a 2D profile
// ---------------------------------------------------------------------------

/**
 * Build a surface of revolution from a 2D profile curve.
 *
 * Profile points are `[axialPos, radius]` pairs ordered by increasing axial
 * position. The surface is revolved 360° around the Z axis.
 *
 * Pole handling: a profile point with `radius ≤ 1e-9` is treated as a single
 * axis vertex and connected to the adjacent ring via N triangles (fan cap)
 * instead of N quads. This produces clean closed ends (e.g. the rounded tip
 * of a bat barrel, the bottom of a knob).
 *
 * Winding: CCW from the outside — barrel faces point radially outward,
 * bottom pole normals point −Z, top pole normals point +Z.
 *
 * Normals: computed via `recomputeLoopNormals` for smooth shading. The
 * auto-smooth threshold is applied by the store when the SceneObject is
 * created, so these normals are angle-independent.
 *
 * UVs: `u` = azimuth fraction [0, 1], `v` = normalised axial position [0, 1].
 *
 * @param profile  Ordered `[axialPos, radius]` pairs (at least 2).
 * @param segments Azimuthal vertex count (clamped to ≥ 3).
 */
export function buildLatheGeometry(
  profile: readonly [number, number][],
  segments: number,
): PrimitiveGeometry {
  const N = Math.max(3, Math.round(segments));
  const P = profile.length;
  const eps = 1e-9;

  if (P < 2) return emptyGeometry();

  // ── Vertex layout ──────────────────────────────────────────────────────────
  // Each profile point is either a pole (1 axis vertex) or a ring (N vertices).
  const isPole = profile.map(([, r]) => r <= eps);
  const rowStart = new Int32Array(P);
  let nVerts = 0;

  for (let i = 0; i < P; i++) {
    rowStart[i] = nVerts;
    nVerts += isPole[i] ? 1 : N;
  }

  // ── Vertices ───────────────────────────────────────────────────────────────
  const verts = new Float32Array(nVerts * 3);

  for (let i = 0; i < P; i++) {
    const [z, r] = profile[i];
    if (isPole[i]) {
      verts[rowStart[i] * 3] = 0;
      verts[rowStart[i] * 3 + 1] = 0;
      verts[rowStart[i] * 3 + 2] = z;
    } else {
      for (let j = 0; j < N; j++) {
        const theta = (2 * Math.PI * j) / N;
        const b = (rowStart[i] + j) * 3;
        verts[b] = Math.cos(theta) * r;
        verts[b + 1] = Math.sin(theta) * r;
        verts[b + 2] = z;
      }
    }
  }

  // ── Face / corner counts ───────────────────────────────────────────────────
  let nFaces = 0;
  let nCorners = 0;

  for (let i = 0; i < P - 1; i++) {
    nFaces += N;
    nCorners += N * (isPole[i] || isPole[i + 1] ? 3 : 4);
  }

  const offsets = new Uint32Array(nFaces + 1);
  const corners = new Uint32Array(nCorners);
  let ci = 0,
    fi = 0;

  // Vertex index helper: pole rows always return their single vertex;
  // ring rows index into the ring by azimuthal slot j (mod N).
  const idx = (row: number, j: number): number =>
    isPole[row] ? rowStart[row] : rowStart[row] + (j % N);

  // ── UVs ────────────────────────────────────────────────────────────────────
  const uvs = new Float32Array(nCorners * 2);
  let ui = 0;

  const z0 = profile[0][0];
  const zSpan = Math.max(1e-9, profile[P - 1][0] - z0);

  for (let i = 0; i < P - 1; i++) {
    const v0 = (profile[i][0] - z0) / zSpan;
    const v1 = (profile[i + 1][0] - z0) / zSpan;

    if (isPole[i]) {
      // Bottom pole → ring: N fan triangles, normals point −Z (outward below).
      // Winding [pole, ring[j+1], ring[j]] — CCW from below.
      for (let j = 0; j < N; j++) {
        const j1 = (j + 1) % N;
        offsets[fi++] = ci;
        corners[ci++] = idx(i, 0); // pole
        corners[ci++] = idx(i + 1, j1);
        corners[ci++] = idx(i + 1, j);
        uvs[ui++] = (j + 0.5) / N;
        uvs[ui++] = v0;
        uvs[ui++] = (j + 1.0) / N;
        uvs[ui++] = v1;
        uvs[ui++] = j / N;
        uvs[ui++] = v1;
      }
    } else if (isPole[i + 1]) {
      // Ring → top pole: N fan triangles, normals point +Z (outward above).
      // Winding [ring[j], ring[j+1], pole] — CCW from above.
      for (let j = 0; j < N; j++) {
        const j1 = (j + 1) % N;
        offsets[fi++] = ci;
        corners[ci++] = idx(i, j);
        corners[ci++] = idx(i, j1);
        corners[ci++] = idx(i + 1, 0); // pole
        uvs[ui++] = j / N;
        uvs[ui++] = v0;
        uvs[ui++] = (j + 1.0) / N;
        uvs[ui++] = v0;
        uvs[ui++] = (j + 0.5) / N;
        uvs[ui++] = v1;
      }
    } else {
      // Ring → ring: N quads, normals point radially outward.
      // Winding [i:j, i:j+1, i+1:j+1, i+1:j] — CCW from outside.
      for (let j = 0; j < N; j++) {
        const j1 = (j + 1) % N;
        offsets[fi++] = ci;
        corners[ci++] = idx(i, j);
        corners[ci++] = idx(i, j1);
        corners[ci++] = idx(i + 1, j1);
        corners[ci++] = idx(i + 1, j);
        uvs[ui++] = j / N;
        uvs[ui++] = v0;
        uvs[ui++] = (j + 1.0) / N;
        uvs[ui++] = v0;
        uvs[ui++] = (j + 1.0) / N;
        uvs[ui++] = v1;
        uvs[ui++] = j / N;
        uvs[ui++] = v1;
      }
    }
  }
  offsets[fi] = ci;

  const topology: PolygonTopology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };

  return {
    vertices: verts,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: recomputeLoopNormals(verts, topology),
    topology,
    uvs,
  };
}

// ---------------------------------------------------------------------------
// buildFrustumShell — hollow frustum (cylinder/cone) shell
// ---------------------------------------------------------------------------

/**
 * Build a hollow frustum shell: an open-topped vessel whose outer wall is
 * shaped by `profile` (via `evalFrustumProfile`) and subdivided by `loops`
 * horizontal ring cuts.
 *
 * Shape coverage:
 *   topRadius  > bottomRadius  →  mixing bowl / cup  (wider opening)
 *   topRadius  ≈ bottomRadius  →  straight cylinder  (bucket / can)
 *   topRadius  < bottomRadius  →  dog bowl / planter (wider base)
 *   floorThickness = 0         →  open tube  (pipe, ring, sleeve)
 *
 * Surface topology (all faces outward-facing):
 *   • Outer wall bands  — (loops+1)×N quads, normals radially outward
 *   • Inner wall bands  — (loops+1)×N quads, normals radially inward
 *   • Rim annulus       — N quads, normals +Z
 *   • Exterior bottom disc  — N triangles, normals −Z  (floorThickness > 0 only)
 *   • Interior floor disc   — N triangles, normals +Z  (floorThickness > 0 only)
 *
 * Normals are computed automatically via `recomputeLoopNormals`.
 *
 * @param loops   Intermediate horizontal ring cuts (0 = base shell only).
 * @param profile Wall-curve profile in [−1, 1] (0 = linear, via evalFrustumProfile).
 */
export function buildFrustumShell(
  topRadius: number,
  bottomRadius: number,
  height: number,
  wallThickness: number,
  floorThickness: number,
  segments: number,
  loops = 0,
  profile = 0,
): PrimitiveGeometry {
  const N = Math.max(3, Math.round(segments));
  const L = Math.max(0, Math.round(loops));
  const nRings = L + 2; // rings including base (0) and rim (L+1)
  const hasFloor = floorThickness > 0;
  const floorZ = hasFloor ? Math.min(floorThickness, height * 0.9) : 0;

  // ── Per-ring radii and heights ─────────────────────────────────────────────
  //
  // Outer ring k: sits at z = k/(L+1) × height, radius = evalFrustumProfile(t, …).
  // Inner ring k: same z as outer except ring 0 (base), which sits at z = floorZ
  //               to close the bottom; radius = outer − wallThickness.
  //
  // Ring k outer vertex base index : 2·k·N
  // Ring k inner vertex base index : (2·k+1)·N

  const outerZ = new Float32Array(nRings);
  const outerR = new Float32Array(nRings);
  const innerZ = new Float32Array(nRings);
  const innerR = new Float32Array(nRings);

  for (let k = 0; k < nRings; k++) {
    const t = k / (nRings - 1); // 0 … 1
    outerZ[k] = t * height;
    outerR[k] = evalFrustumProfile(t, bottomRadius, topRadius, height, profile);

    if (k === 0 && hasFloor) {
      // Base inner ring sits at floorZ so the floor disc closes the bottom
      innerZ[k] = floorZ;
      const tFloor = height > 0 ? floorZ / height : 0;
      innerR[k] = Math.max(
        0.001,
        evalFrustumProfile(tFloor, bottomRadius, topRadius, height, profile) -
          wallThickness,
      );
    } else {
      innerZ[k] = outerZ[k];
      innerR[k] = Math.max(0.001, outerR[k] - wallThickness);
    }
  }

  // ── Vertices ──────────────────────────────────────────────────────────────
  const extraVerts = hasFloor ? 2 : 0;
  const verts = new Float32Array((2 * nRings * N + extraVerts) * 3);

  for (let k = 0; k < nRings; k++) {
    for (let i = 0; i < N; i++) {
      const theta = (2 * Math.PI * i) / N;
      const cos = Math.cos(theta);
      const sin_ = Math.sin(theta);

      const ob = (2 * k * N + i) * 3;
      verts[ob] = cos * outerR[k];
      verts[ob + 1] = sin_ * outerR[k];
      verts[ob + 2] = outerZ[k];

      const ib = ((2 * k + 1) * N + i) * 3;
      verts[ib] = cos * innerR[k];
      verts[ib + 1] = sin_ * innerR[k];
      verts[ib + 2] = innerZ[k];
    }
  }

  if (hasFloor) {
    const eb = 2 * nRings * N * 3;
    verts[eb] = 0;
    verts[eb + 1] = 0;
    verts[eb + 2] = 0;
    const ib = (2 * nRings * N + 1) * 3;
    verts[ib] = 0;
    verts[ib + 1] = 0;
    verts[ib + 2] = floorZ;
  }

  // Index helpers
  const outerIdx = (k: number, i: number) => 2 * k * N + (i % N);
  const innerIdx = (k: number, i: number) => (2 * k + 1) * N + (i % N);
  const V_EXT = 2 * nRings * N;
  const V_INT = 2 * nRings * N + 1;

  // ── Topology ───────────────────────────────────────────────────────────────
  const nBands = nRings - 1;
  const quadFaces = (2 * nBands + 1) * N; // outer bands + inner bands + rim
  const triFaces = hasFloor ? 2 * N : 0;
  const totalFaces = quadFaces + triFaces;
  const totalCorners = quadFaces * 4 + triFaces * 3;

  const offsets = new Uint32Array(totalFaces + 1);
  const corners = new Uint32Array(totalCorners);
  let ci = 0,
    fi = 0;

  // Outer wall bands — CCW from outside → normals outward
  for (let k = 0; k < nBands; k++) {
    for (let i = 0; i < N; i++) {
      offsets[fi++] = ci;
      corners[ci++] = outerIdx(k, i);
      corners[ci++] = outerIdx(k, i + 1);
      corners[ci++] = outerIdx(k + 1, i + 1);
      corners[ci++] = outerIdx(k + 1, i);
    }
  }

  // Inner wall bands — CCW from inside (top→bottom) → normals inward
  for (let k = 0; k < nBands; k++) {
    for (let i = 0; i < N; i++) {
      offsets[fi++] = ci;
      corners[ci++] = innerIdx(k + 1, i);
      corners[ci++] = innerIdx(k + 1, i + 1);
      corners[ci++] = innerIdx(k, i + 1);
      corners[ci++] = innerIdx(k, i);
    }
  }

  // Rim annulus — CCW from above → normals +Z
  const topK = nRings - 1;
  for (let i = 0; i < N; i++) {
    offsets[fi++] = ci;
    corners[ci++] = outerIdx(topK, i);
    corners[ci++] = outerIdx(topK, i + 1);
    corners[ci++] = innerIdx(topK, i + 1);
    corners[ci++] = innerIdx(topK, i);
  }

  if (hasFloor) {
    // Exterior bottom disc — CCW from below → normals −Z
    for (let i = 0; i < N; i++) {
      offsets[fi++] = ci;
      corners[ci++] = V_EXT;
      corners[ci++] = outerIdx(0, i + 1);
      corners[ci++] = outerIdx(0, i);
    }
    // Interior floor disc — CCW from above → normals +Z
    for (let i = 0; i < N; i++) {
      offsets[fi++] = ci;
      corners[ci++] = V_INT;
      corners[ci++] = innerIdx(0, i);
      corners[ci++] = innerIdx(0, i + 1);
    }
  }

  offsets[fi] = ci;

  const topology: PolygonTopology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };

  // ── UVs ────────────────────────────────────────────────────────────────────
  const uvData = new Float32Array(totalCorners * 2);
  let ui = 0;

  // Outer wall bands: u = azimuth, v = normalised height
  for (let k = 0; k < nBands; k++) {
    const v0 = height > 0 ? outerZ[k] / height : 0;
    const v1 = height > 0 ? outerZ[k + 1] / height : 1;
    for (let i = 0; i < N; i++) {
      const u0 = i / N,
        u1 = (i + 1) / N;
      uvData[ui++] = u0;
      uvData[ui++] = v0;
      uvData[ui++] = u1;
      uvData[ui++] = v0;
      uvData[ui++] = u1;
      uvData[ui++] = v1;
      uvData[ui++] = u0;
      uvData[ui++] = v1;
    }
  }

  // Inner wall bands: same azimuth/height mapping (winding is reversed, UVs are not)
  for (let k = 0; k < nBands; k++) {
    const v0 = height > 0 ? innerZ[k] / height : 0;
    const v1 = height > 0 ? innerZ[k + 1] / height : 1;
    for (let i = 0; i < N; i++) {
      const u0 = i / N,
        u1 = (i + 1) / N;
      uvData[ui++] = u0;
      uvData[ui++] = v1;
      uvData[ui++] = u1;
      uvData[ui++] = v1;
      uvData[ui++] = u1;
      uvData[ui++] = v0;
      uvData[ui++] = u0;
      uvData[ui++] = v0;
    }
  }

  // Rim annulus: u = azimuth, v = 0 (outer) → 1 (inner)
  for (let i = 0; i < N; i++) {
    const u0 = i / N,
      u1 = (i + 1) / N;
    uvData[ui++] = u0;
    uvData[ui++] = 0;
    uvData[ui++] = u1;
    uvData[ui++] = 0;
    uvData[ui++] = u1;
    uvData[ui++] = 1;
    uvData[ui++] = u0;
    uvData[ui++] = 1;
  }

  if (hasFloor) {
    // Exterior bottom disc — circular polar projection
    for (let i = 0; i < N; i++) {
      const a0 = (2 * Math.PI * i) / N;
      const a1 = (2 * Math.PI * (i + 1)) / N;
      uvData[ui++] = 0.5;
      uvData[ui++] = 0.5;
      uvData[ui++] = 0.5 + 0.5 * Math.cos(a1);
      uvData[ui++] = 0.5 + 0.5 * Math.sin(a1);
      uvData[ui++] = 0.5 + 0.5 * Math.cos(a0);
      uvData[ui++] = 0.5 + 0.5 * Math.sin(a0);
    }
    // Interior floor disc — circular polar projection
    for (let i = 0; i < N; i++) {
      const a0 = (2 * Math.PI * i) / N;
      const a1 = (2 * Math.PI * (i + 1)) / N;
      uvData[ui++] = 0.5;
      uvData[ui++] = 0.5;
      uvData[ui++] = 0.5 + 0.5 * Math.cos(a0);
      uvData[ui++] = 0.5 + 0.5 * Math.sin(a0);
      uvData[ui++] = 0.5 + 0.5 * Math.cos(a1);
      uvData[ui++] = 0.5 + 0.5 * Math.sin(a1);
    }
  }

  return {
    vertices: verts,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: recomputeLoopNormals(verts, topology),
    topology,
    uvs: uvData,
  };
}
